// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { DocumentModel, ProductModel, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export interface CustomEventData {
    duration: string;
    coordinatorEmail: string;
    coordinatorInitial: string;
    category: string;
    endDate: string;
    startDate: string;
    endDateWithOffset: string;
    startDateWithOffset: string;
    location: string;
    thirdPartyRegistration: string;
    formType?: string;
    eventCity: string;
    eventState: string;
}

export interface RegionalInventory {
    isLoading: boolean;
    amount?: string;
}

export interface ProductPromotionMessage {
    message: string;
    color: string;
}

export interface DocumentGroup {
    groupName: string;
    documents: DocumentModel[];
}

export interface VideoDocumentModel {
    id: number;
    thumbnailPath?: string;
    link?: string;
}

export type CustomProductState = {
    productId: string;
    videos: VideoDocumentModel[];
    selectedVideoIndex: number;
    specSheetLink?: string;
    documentGroups?: DocumentGroup[];
    promotionMessage?: ProductPromotionMessage;
    isEvent?: boolean;
    eventData?: CustomEventData;
    quickShipIconUrl?: string;
    regionalInventory?: RegionalInventory;
};

export type XPorductDetailsState = {
    byId: Dictionary<CustomProductState>;
};

const initialState: XPorductDetailsState = {
    byId: {},
};

const xProductDetails = createTypedReducerWithImmer(initialState, {
    "xProductDetails/SetCustomProductState": (
        draft: Draft<XPorductDetailsState>,
        action: { customProductStates: CustomProductState[] },
    ) => {
        action.customProductStates.every(customProductState => {
            draft.byId[customProductState.productId] = {
                ...draft.byId[customProductState.productId],
                ...customProductState,
            };
            return true;
        });
    },
    "xProductDetails/SetSelectedVideoIndex": (
        draft: Draft<XPorductDetailsState>,
        action: { index: number; product: ProductModel },
    ) => {
        draft.byId[action.product.id].selectedVideoIndex = action.index;
    },
    "xProductDetails/SetLoadingRegionalInventory": (draft: Draft<XPorductDetailsState>, action: { ids: string[] }) => {
        const regionalInventory = {
            isLoading: true,
        };
        action.ids?.every(id => {
            draft.byId[id] = {
                ...draft.byId[id],
                regionalInventory,
            };

            return true;
        });
    },
    "xProductDetails/SetRegionalInventory": (
        draft: Draft<XPorductDetailsState>,
        action: { results: RealTimeInventoryModel },
    ) => {
        action.results.realTimeInventoryResults?.every(result => {
            const regionalInventory = {
                isLoading: false,
                amount: result.additionalResults?.regionalInventory,
            };
            draft.byId[result.productId] = {
                ...draft.byId[result.productId],
                regionalInventory,
            };
            return true;
        });
    },
});

type xProductDetailsReducers = {
    xProductDetails: typeof xProductDetails;
} & typeof reducers;

(reducers as xProductDetailsReducers).xProductDetails = xProductDetails;
